import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  clickToLinkHandler,
  currencyFormat,
  getPrimaryOrganizationIdForUser,
} from "../../helpers/utils";
import "./styles/index.scss";
import Button from "../common/forms/button";
import * as api from "../../api/generated-api";
import { InvestorAvatar } from "./investorsDeals/DealInvestors/investorAvatar";
import { configureAuthHeader, configureBaseUrl } from "../../api/axiosConfig";
import { getDeals, getInvestorsForAllDeals } from "../../api/deals/deals";
import { isInvestorInAtLeastOneOrg } from "../../helpers/utilsTS";
import { UserWithPersonWithOrganizationPersons } from "../../api/types";

type DealsBoardPageProps = {
  user: UserWithPersonWithOrganizationPersons;
  token: string;
};

const DealsBoardPage = (props: DealsBoardPageProps) => {
  const { user, token } = props;

  configureBaseUrl();
  configureAuthHeader(token);

  const defaultOrgIdForUser = getPrimaryOrganizationIdForUser(user);

  const isInvestor = isInvestorInAtLeastOneOrg(
    user.person.organizations_persons
  );

  const [deals, setDeals] = useState<api.Deal[]>(null);
  const [investorsByDealId, setInvestorsByDealId] =
    useState<Map<number, api.DealInvestor[]>>(null);

  useEffect(() => {
    getDeals((fetchedDeals) => {
      setDeals(fetchedDeals);
    });
  }, []);

  useEffect(() => {
    if (deals && deals.length > 0) {
      getInvestorsForAllDeals(deals, (fetchedInvestorsByDealId) => {
        setInvestorsByDealId(fetchedInvestorsByDealId);
      });
    }
  }, [deals]);

  if (!deals || (deals.length != 0 && !investorsByDealId)) {
    return null;
  }

  return (
    <div className="deals-board">
      <div className="deals-title-container">
        <div className="deals-title">Deals</div>
        {!isInvestor && (
          <Button
            onClick={clickToLinkHandler.bind(
              this,
              `/${defaultOrgIdForUser}/deals/before-create`
            )}
            type="primary"
            size="medium"
          >
            New Deal
          </Button>
        )}
      </div>

      <React.Fragment>
        {deals.length ? (
          <div className="deals-card-container">
            <div className="deals-container">
              {deals.map((deal, key) => {
                const investorsForDeal = investorsByDealId.get(deal.id);
                const widthOfInvestorsContainer =
                  (investorsForDeal.length &&
                    40 + 31 * (investorsForDeal.length - 1)) ||
                  0;
                return (
                  <div
                    key={"deal-" + key}
                    id={`deal-card-${deal.id}`}
                    className="deal-card"
                    data-testid="deal-card"
                    onClick={clickToLinkHandler.bind(
                      this,
                      `/${deal.organizationId}/deals/${deal.id}/edit/deal-information`
                    )}
                  >
                    <div className="deal-description">{deal.companyName}</div>
                    <div className="separator" />
                    <div className="deal-content">
                      <div className="left-column">
                        <div className="title">
                          Series of Preferred Stock Sold
                        </div>
                        <div className="value">
                          {deal.seriesOfPreferredStockSold}
                        </div>
                        <div className="row">
                          <div className="column">
                            <div className="title">Pre-Money Valuation</div>
                            <div className="value">
                              {currencyFormat(deal.valuation)}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="right-column">
                        <div className="title">Total Round Size</div>
                        <div className="value">
                          {currencyFormat(deal.totalRoundSize)}
                        </div>
                      </div>
                    </div>
                    <div className="separator without-margin" />
                    <div className="investors-container">
                      <div className="investors-count">
                        <div className="title">Investors</div>
                        <div className="count">{investorsForDeal.length}</div>
                      </div>
                      <div
                        className="investors-avatars"
                        style={{
                          width: `${widthOfInvestorsContainer}px`,
                        }}
                      >
                        {!!investorsForDeal.length &&
                          investorsForDeal.map((i, key2) => {
                            const left = key2 * 31;
                            return (
                              <InvestorAvatar
                                key={"investor_" + key2}
                                label={i.name.charAt(0)}
                                styles={{
                                  left: `${left}px`,
                                }}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  clickToLinkHandler(
                                    "/" +
                                      deal.organizationId +
                                      "/deals/" +
                                      deal.id +
                                      "/edit/investors/edit/" +
                                      i.id
                                  );
                                }}
                              />
                            );
                          })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="deals-empty-dashboard">
            <div className="deals-empty-dashboard-block-container">
              <div className="deals-empty-dashboard-block">
                <div className="deals-empty-dashboard-block-title">01</div>
                <div className="deals-empty-dashboard-icon-1" />
                <div className="deals-empty-dashboard-block-text">
                  Use Dealkit templates and generate documents in seconds
                </div>
              </div>
            </div>
            <div className="deals-empty-dashboard-block-container">
              <div className="deals-empty-dashboard-block">
                <div className="deals-empty-dashboard-block-title">02</div>
                <div className="deals-empty-dashboard-icon-2" />
                <div className="deals-empty-dashboard-block-text">
                  Track deal status with automated redlining and transparent
                  revision history
                </div>
              </div>
            </div>
            <div className="deals-empty-dashboard-block-container">
              <div className="deals-empty-dashboard-block">
                <div className="deals-empty-dashboard-block-title">03</div>
                <div className="deals-empty-dashboard-icon-3" />
                <div className="deals-empty-dashboard-block-text">
                  Manage next steps for a streamlined closing process
                </div>
              </div>
            </div>
          </div>
        )}
      </React.Fragment>
    </div>
  );
};

const mapStateToProps = (state) => {
  const { token, user } = state.auth;
  return {
    user,
    token,
  };
};

export default connect(mapStateToProps)(DealsBoardPage);
